import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/** Internal. */
import { parseDocument, DOCUMENT_STATUS } from 'shared/api/documentUploadApi';
import { readSocketData } from 'shared/lib/fetchSocket';
import { randomId } from '@mui/x-data-grid-generator';

import {
    MAX_FILES,
    findMblFile,
    getHblNumberKey,
    getParsedDataBase,
    isAirTab,
    isHblOrHawb,
    isOceanExportTab,
    isOceanTab,
} from './documentUploadUtils';

/* ================================================================== *
 * useDocumentParsing — file queue + PDF parsing
 * ================================================================== */

/** Owns the file list, drag state, batch parsing and the derived success/failed views. */
export function useDocumentParsing({ open, tabValue, sid, getAccessTokenSilently }) {
    /** Files: { id, file, name, status, progress, parsedData, document_type, error, previewEdited } */
    const [files, setFiles] = useState([]);
    const [dragging, setDragging] = useState(false);
    const processedIdsRef = useRef(new Set());

    const addFiles = useCallback((fileList) => {
        const pdfs = Array.from(fileList || []).filter((f) => f.name.toLowerCase().endsWith('.pdf'));
        if (pdfs.length === 0) return;
        setFiles((prev) => {
            const remain = MAX_FILES - prev.length;
            const added = pdfs.slice(0, remain).map((file) => ({
                id: randomId(),
                file,
                name: file.name,
                status: DOCUMENT_STATUS.PENDING,
                progress: 0,
                parsedData: null,
                error: null,
            }));
            return [...prev, ...added];
        });
    }, []);

    const removeFile = useCallback((id) => {
        setFiles((prev) => prev.filter((f) => f.id !== id));
    }, []);

    const removeSuccessFiles = useCallback(() => {
        setFiles((prev) => prev.filter((f) => f.status !== DOCUMENT_STATUS.SUCCESS));
    }, []);

    const resetFiles = useCallback(() => {
        processedIdsRef.current = new Set();
        setFiles([]);
    }, []);

    const dragHandlers = useMemo(
        () => ({
            onDrop: (e) => {
                e.preventDefault();
                setDragging(false);
                addFiles(e.dataTransfer?.files);
            },
            onDragOver: (e) => {
                e.preventDefault();
                setDragging(true);
            },
            onDragLeave: (e) => {
                e.preventDefault();
                setDragging(false);
            },
        }),
        [addFiles]
    );

    const handleFileInputChange = useCallback(
        (e) => {
            addFiles(e.target?.files);
            e.target.value = '';
        },
        [addFiles]
    );

    /**
     * Patch a single file entry in place.
     * Returns the previous array untouched when the entry is gone (dialog closed mid-parse), so a late
     * parse response cannot re-render the dialog. The request itself is left alone — the server finishes
     * the parse either way, so cancelling it would buy nothing.
     */
    const patchFile = useCallback((id, patch) => {
        setFiles((prev) =>
            prev.some((f) => f.id === id) ? prev.map((f) => (f.id === id ? { ...f, ...patch } : f)) : prev
        );
    }, []);

    const runParse = useCallback(
        async (item) => {
            patchFile(item.id, { status: DOCUMENT_STATUS.PARSING, progress: 0 });
            try {
                const result = await parseDocument({
                    file: item.file,
                    shipmentType: tabValue,
                    autoCreate: false,
                    sid,
                    getAccessTokenSilently,
                    onUploadProgress: (pct) => patchFile(item.id, { progress: pct }),
                });
                if (result.status === 200 && result.parsed_data) {
                    patchFile(item.id, {
                        status: DOCUMENT_STATUS.SUCCESS,
                        progress: 100,
                        parsedData: result.parsed_data,
                        document_type: result.document_type ?? 'UNKNOWN',
                        error: null,
                    });
                } else {
                    patchFile(item.id, {
                        status: DOCUMENT_STATUS.FAILED,
                        error: result.message || 'Parsing failed',
                    });
                }
            } catch (err) {
                patchFile(item.id, {
                    status: DOCUMENT_STATUS.FAILED,
                    error: err.response?.data?.message || err.message || 'Upload failed',
                });
            }
        },
        [tabValue, sid, getAccessTokenSilently, patchFile]
    );

    const processBatch = useCallback(async () => {
        const pending = files.filter(
            (f) => f.status === DOCUMENT_STATUS.PENDING && !processedIdsRef.current.has(f.id)
        );
        pending.forEach((f) => processedIdsRef.current.add(f.id));
        await Promise.all(pending.map((item) => runParse(item)));
    }, [files, runParse]);

    useEffect(() => {
        if (!open || files.length === 0) return;
        const hasPending = files.some((f) => f.status === DOCUMENT_STATUS.PENDING);
        if (hasPending) processBatch();
    }, [open, files, processBatch]);

    const successFiles = useMemo(() => files.filter((f) => f.status === DOCUMENT_STATUS.SUCCESS), [files]);
    const hasFailed = files.some((f) => f.status === DOCUMENT_STATUS.FAILED);

    return {
        files,
        successFiles,
        hasFailed,
        dragging,
        dragHandlers,
        addFiles,
        removeFile,
        removeSuccessFiles,
        resetFiles,
        handleFileInputChange,
    };
}

/* ================================================================== *
 * useMblHblSync — parsed grid rows + user cell edits
 * ================================================================== */

/** Location value can arrive as a resolved object or a plain string; keep objects, stringify the rest. */
const toLocationValue = (value) => (value && typeof value === 'object' ? value : value != null ? String(value) : '');

const toStringValue = (value) => (value != null ? String(value) : '');

const toList = (value) => (Array.isArray(value) ? value : value != null ? [value] : []);

/**
 * Grid row for the parsed MBL/MAWB file, or null when the batch has no master document.
 * The row id carries the file id so cell edits are dropped when the master file is replaced.
 */
function buildMblRow(file, tabValue) {
    const mblData = getParsedDataBase(file);
    const id = `${file.id}-mbl`;
    if (isAirTab(tabValue)) {
        return {
            id,
            mawb: mblData.mawb ?? mblData.mbl ?? '',
            etd: toStringValue(mblData.etd),
            eta: toStringValue(mblData.eta),
            aol: toLocationValue(mblData.aol),
            aod: toLocationValue(mblData.aod),
            faod: toLocationValue(mblData.faod),
        };
    }
    return {
        id,
        pol: toLocationValue(mblData.pol),
        pod: toLocationValue(mblData.pod),
        fpod: toLocationValue(mblData.fpod),
        vessel:
            mblData.vessel && typeof mblData.vessel === 'object'
                ? mblData.vessel.name ?? ''
                : toStringValue(mblData.vessel),
        etd: toStringValue(mblData.etd),
        eta: toStringValue(mblData.eta),
        mbl: mblData.mbl ?? mblData.mawb ?? '',
    };
}

/** One grid row per container of an ocean house bill (placeholder row when the parser found none). */
function buildOceanRows(fileId, hblNumber, containers, tabValue) {
    const hblNumKey = getHblNumberKey(tabValue);
    const withSeal = isOceanExportTab(tabValue);
    if (containers.length === 0) {
        return [
            {
                id: fileId,
                fileId,
                [hblNumKey]: hblNumber,
                containerSummary: '-',
                containerVolume: '-',
                ...(withSeal && { containerSeal: '-' }),
            },
        ];
    }
    return containers.map((container, index) => ({
        id: `${fileId}-${index}`,
        fileId,
        [hblNumKey]: hblNumber,
        containerSummary: container?.number ?? container?.cntr_number ?? '',
        containerVolume: container?.volume ?? container?.size ?? '',
        ...(withSeal && { containerSeal: container?.seal ?? '' }),
    }));
}

/** One grid row per alias of an air/trucking house bill (empty row when the parser found none). */
function buildAliasRows(fileId, hblNumber, aliases, tabValue) {
    const hblNumKey = getHblNumberKey(tabValue);
    if (aliases.length === 0) {
        return [
            {
                id: fileId,
                fileId,
                [hblNumKey]: hblNumber,
                alias: '',
                flight: '',
                volume: '',
                unit: '',
                weight: '',
            },
        ];
    }
    return aliases.map((alias, index) => ({
        id: `${fileId}-${index}`,
        fileId,
        [hblNumKey]: hblNumber,
        alias: alias?.alias ?? '',
        flight: alias?.flight ?? alias?.il_opus_vessel_id ?? '',
        volume: alias?.volume != null ? alias.volume : alias?.quantity != null ? alias.quantity : '',
        unit: alias?.unit ?? alias?.il_opus_volume_unit_id ?? '',
        weight: alias?.weight != null ? alias.weight : '',
    }));
}

/**
 * Derives the MBL and HBL grid rows from the parsed files and layers the user's cell edits on top.
 * Parsed rows stay a useMemo (no sync effect, so no first-render flicker or stale values); edits are
 * the only state and are keyed by grid row id, which is derived from the file id — so replacing a
 * file drops its edits instead of applying them to the next file's row.
 */
export function useMblHblSync({ successFiles, tabValue }) {
    /** Edited rows per grid row id: { [rowId]: row }. */
    const [rowEdits, setRowEdits] = useState({});

    const mblFile = useMemo(() => findMblFile(successFiles), [successFiles]);

    const baseMblRow = useMemo(() => (mblFile ? buildMblRow(mblFile, tabValue) : null), [mblFile, tabValue]);

    const hblFiles = useMemo(() => successFiles.filter((f) => isHblOrHawb(f.document_type)), [successFiles]);

    const baseHblRows = useMemo(() => {
        const hblNumKey = getHblNumberKey(tabValue);
        return hblFiles.flatMap((file) => {
            const parsed = getParsedDataBase(file);
            const hblNumber = parsed[hblNumKey] ?? parsed.hbl ?? parsed.hawb ?? '';
            if (isOceanTab(tabValue)) {
                return buildOceanRows(file.id, hblNumber, toList(parsed.containers), tabValue);
            }
            return buildAliasRows(file.id, hblNumber, toList(parsed.aliases), tabValue);
        });
    }, [hblFiles, tabValue]);

    const applyEdits = useCallback((row) => (rowEdits[row.id] ? { ...row, ...rowEdits[row.id] } : row), [rowEdits]);

    const editedMblRow = useMemo(() => (baseMblRow ? applyEdits(baseMblRow) : null), [baseMblRow, applyEdits]);

    const editedHblRows = useMemo(() => baseHblRows.map(applyEdits), [baseHblRows, applyEdits]);

    const processMblRowUpdate = useCallback((newRow) => {
        setRowEdits((prev) => ({ ...prev, [newRow.id]: newRow }));
        return newRow;
    }, []);

    const processHblRowUpdate = useCallback(
        (newRow) => {
            const hblNumKey = getHblNumberKey(tabValue);
            setRowEdits((prev) => {
                const next = { ...prev, [newRow.id]: newRow };
                /** The house bill number is shared by every row of the same file. */
                if (newRow[hblNumKey] != null) {
                    baseHblRows.forEach((row) => {
                        if (row.fileId !== newRow.fileId || row.id === newRow.id) return;
                        next[row.id] = { ...(next[row.id] ?? row), [hblNumKey]: newRow[hblNumKey] };
                    });
                }
                return next;
            });
            return newRow;
        },
        [tabValue, baseHblRows]
    );

    const resetEdits = useCallback(() => setRowEdits({}), []);

    return {
        mblFile,
        editedMblRow,
        editedHblRows,
        processMblRowUpdate,
        processHblRowUpdate,
        resetEdits,
    };
}

/* ================================================================== *
 * useLocationAutocomplete — vessel + location option lists
 * ================================================================== */

/** Location fields of the MBL row: ocean uses pol/pod/fpod, air uses aol/aod/faod. */
export const LOCATION_FIELDS = ['pol', 'pod', 'fpod', 'aol', 'aod', 'faod'];

const EMPTY_LOCATION_OPTIONS = LOCATION_FIELDS.reduce((acc, field) => ({ ...acc, [field]: [] }), {});

/**
 * Autocomplete options for the MBL row: vessel from il_opus_vessel, every location field from il_location.
 * Returns one option list and one input handler per field instead of 7 duplicated state/handler pairs.
 */
export function useLocationAutocomplete({ dispatch, tabValue }) {
    const [vesselOptions, setVesselOptions] = useState([]);
    const [locationOptions, setLocationOptions] = useState(EMPTY_LOCATION_OPTIONS);

    const fetchLocationOptions = useCallback(
        (field, value) => {
            readSocketData(dispatch, tabValue, 'cell', 'il_location', '', value ?? '', (data) => {
                setLocationOptions((prev) => ({ ...prev, [field]: Array.isArray(data) ? data : [] }));
            });
        },
        [dispatch, tabValue]
    );

    const handleVesselInputChange = useCallback(
        (event, value) => {
            readSocketData(dispatch, tabValue, 'cell', 'il_opus_vessel', '', value ?? '', (data) => {
                setVesselOptions(Array.isArray(data) ? data : []);
            });
        },
        [dispatch, tabValue]
    );

    const locationInputHandlers = useMemo(
        () =>
            LOCATION_FIELDS.reduce(
                (acc, field) => ({
                    ...acc,
                    [field]: (event, value) => fetchLocationOptions(field, value),
                }),
                {}
            ),
        [fetchLocationOptions]
    );

    const resetOptions = useCallback(() => {
        setVesselOptions([]);
        setLocationOptions(EMPTY_LOCATION_OPTIONS);
    }, []);

    return {
        vesselOptions,
        locationOptions,
        handleVesselInputChange,
        locationInputHandlers,
        resetOptions,
    };
}
